import { useEffect, useMemo, useState } from 'react';
import { fetchBatchesForItem, type BatchStockRecord } from '@/api/batch.api';
import type { IssueQueueItem, IssueBatchPick } from '@/api/issueQueue.api';

// Issue screen of a stage's materials (or one material): required, already issued, pending and
// available now, and the quantity to issue. Less than pending is a partial issue — the rest stays
// pending on the line and is issued when stock arrives. Batch-tracked material is issued from its
// batches: oldest first (FIFO) unless the store keeper picks them.

interface Props {
  open: boolean;
  lines: IssueQueueItem[];
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (result: { quantities: Record<string, number>; picks: Record<string, IssueBatchPick[]> }) => void;
}

type Mode = 'FIFO' | 'PICK';
const round6 = (n: number) => Math.round(n * 1e6) / 1e6;
const pendingOf = (l: IssueQueueItem) => Math.max(0, round6(Number(l.pending_qty ?? (Number(l.required_qty) - Number(l.issued_qty || 0)))));
const freeOf = (l: IssueQueueItem) => Math.max(0, Number(l.free_qty ?? l.stock_available ?? 0));

export default function IssueBatchModal({ open, lines, busy, onCancel, onConfirm }: Props) {
  const tracked = useMemo(() => lines.filter((l) => l.is_batch_tracked), [lines]);
  const [batches, setBatches] = useState<Record<string, BatchStockRecord[]>>({});
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<Record<string, Mode>>({});
  const [picked, setPicked] = useState<Record<string, Record<string, string>>>({});
  const [issueQty, setIssueQty] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setMode({});
    setPicked({});
    // Default: everything pending, or what is available when short
    setIssueQty(Object.fromEntries(lines.map((l) => [l.id, String(round6(Math.min(pendingOf(l), freeOf(l))))])));
    if (!tracked.length) { setBatches({}); return; }
    setLoading(true);
    Promise.all(tracked.map(async (l) =>
      [l.id, await fetchBatchesForItem(l.item_id, l.issue_warehouse_id || l.warehouse_id || undefined)] as const))
      .then((rows) => setBatches(Object.fromEntries(rows)))
      .finally(() => setLoading(false));
  }, [open, lines, tracked]);

  if (!open) return null;

  const qtyOf = (l: IssueQueueItem) => Number(issueQty[l.id] || 0);
  const usable = (lineId: string) => (batches[lineId] || []).filter((b) => b.currentQty > 0);
  const fifo = (line: IssueQueueItem) => {
    let left = qtyOf(line);
    const out: { batch: BatchStockRecord; qty: number }[] = [];
    for (const b of usable(line.id)) {
      if (left <= 1e-6) break;
      const take = round6(Math.min(b.currentQty, left));
      out.push({ batch: b, qty: take });
      left = round6(left - take);
    }
    return { out, short: left > 1e-6 ? left : 0 };
  };
  const pickedTotal = (lineId: string) => Object.values(picked[lineId] || {}).reduce((s, v) => s + (Number(v) || 0), 0);

  const lineError = (line: IssueQueueItem): string | null => {
    const q = qtyOf(line);
    if (q < 0) return 'Quantity cannot be negative';
    if (q > pendingOf(line) + 1e-6) return `Only ${pendingOf(line)} ${line.unit} is pending`;
    if (q > freeOf(line) + 1e-6) return `Only ${freeOf(line)} ${line.unit} is available`;
    if (!line.is_batch_tracked || q <= 0) return null;
    if ((mode[line.id] || 'FIFO') === 'FIFO') {
      const { short } = fifo(line);
      return short > 0 ? `Usable batches are ${round6(short)} ${line.unit} short` : null;
    }
    for (const b of usable(line.id)) {
      if (Number(picked[line.id]?.[b.id] || 0) > b.currentQty + 1e-6) return `Batch ${b.batchNumber} has only ${b.currentQty} ${line.unit}`;
    }
    const total = round6(pickedTotal(line.id));
    return Math.abs(total - q) > 1e-6 ? `Picked ${total} of ${q} ${line.unit}` : null;
  };
  const errors = lines.map(lineError);
  const anyQty = lines.some((l) => qtyOf(l) > 0);
  const partial = lines.some((l) => qtyOf(l) < pendingOf(l) - 1e-6);
  const ready = !loading && anyQty && errors.every((e) => !e);

  const confirm = () => {
    const quantities: Record<string, number> = {};
    const picks: Record<string, IssueBatchPick[]> = {};
    for (const line of lines) {
      quantities[line.id] = round6(qtyOf(line));
      if (line.is_batch_tracked && qtyOf(line) > 0 && (mode[line.id] || 'FIFO') === 'PICK') {
        picks[line.id] = Object.entries(picked[line.id] || {})
          .filter(([, v]) => Number(v) > 0)
          .map(([batchId, v]) => ({ batchId, qty: Number(v) }));
      }
    }
    onConfirm({ quantities, picks });
  };

  const first = lines[0];

  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={busy ? undefined : onCancel} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-base font-bold text-slate-900">Issue Material</h3>
            {first && (
              <p className="text-xs text-slate-500 mt-0.5">
                {first.po_number ? `${first.po_number} → ` : ''}Stage {first.stage_number}{first.stage_name ? `: ${first.stage_name}` : ''}
              </p>
            )}
          </div>
          <button type="button" onClick={onCancel} disabled={busy}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 text-slate-500">
                  {['Item', 'Required', 'Issued', 'Pending', 'Available', 'Issue Now', ''].map((h) => (
                    <th key={h} className={`px-3 py-2 font-semibold uppercase tracking-wide whitespace-nowrap ${h === 'Item' || h === '' ? 'text-left' : 'text-right'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {lines.map((l, i) => {
                  const pend = pendingOf(l);
                  const free = freeOf(l);
                  const short = free + 1e-6 < pend;
                  return (
                    <tr key={l.id} className="align-top">
                      <td className="px-3 py-2">
                        <p className="font-semibold text-slate-800">{l.item_name}</p>
                        {l.is_batch_tracked && <span className="text-[10px] text-indigo-600 font-semibold">Batch tracked</span>}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{Number(l.required_qty)} {l.unit}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{Number(l.issued_qty || 0)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap font-semibold">{pend}</td>
                      <td className={`px-3 py-2 text-right whitespace-nowrap ${short ? 'text-amber-700 font-semibold' : 'text-slate-700'}`}>{free}</td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" min={0} max={Math.min(pend, free)} step="any"
                          value={issueQty[l.id] ?? ''}
                          onKeyDown={(e) => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }}
                          onChange={(e) => setIssueQty((p) => ({ ...p, [l.id]: e.target.value }))}
                          className="w-24 h-8 px-2 rounded-md border border-slate-200 text-right focus:outline-none focus:border-indigo-500" />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {errors[i] ? (
                          <span className="text-rose-600 font-semibold"><i className="ri-error-warning-line" /> {errors[i]}</span>
                        ) : short ? (
                          <span className="text-amber-700 font-semibold"><i className="ri-alert-line" /> Short</span>
                        ) : (
                          <span className="text-emerald-700 font-semibold"><i className="ri-check-line" /> OK</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {lines.some((l) => freeOf(l) + 1e-6 < pendingOf(l)) && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <i className="ri-alert-line" /> Some material is short. Issue what is available now (partial — the rest stays pending
              and is issued when stock arrives), or set the quantity to 0 / cancel to wait for full stock.
            </p>
          )}

          {/* Batches of batch-tracked material, for the quantity issued now */}
          {loading && <p className="text-sm text-slate-400"><i className="ri-loader-4-line animate-spin" /> Loading batches…</p>}
          {!loading && tracked.filter((l) => qtyOf(l) > 0).map((line) => {
            const m = mode[line.id] || 'FIFO';
            const list = usable(line.id);
            const { out } = fifo(line);
            return (
              <div key={line.id} className="border border-slate-200 rounded-lg">
                <div className="flex items-center justify-between gap-3 px-3 py-2.5 bg-slate-50 border-b border-slate-200 rounded-t-lg">
                  <p className="text-sm font-semibold text-slate-900">{line.item_name} — batches for {qtyOf(line)} {line.unit}</p>
                  <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs font-semibold shrink-0">
                    {(['FIFO', 'PICK'] as Mode[]).map((opt) => (
                      <button key={opt} type="button" onClick={() => setMode((p) => ({ ...p, [line.id]: opt }))}
                        className={`px-2.5 py-1 rounded-md cursor-pointer ${m === opt ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                        {opt === 'FIFO' ? 'FIFO (auto)' : 'Pick batches'}
                      </button>
                    ))}
                  </div>
                </div>
                {list.length === 0 ? (
                  <p className="px-3 py-3 text-xs text-rose-600">No usable batch of this item in the issuing warehouse.</p>
                ) : m === 'FIFO' ? (
                  <ul className="px-3 py-2 text-xs divide-y divide-slate-100">
                    {out.map(({ batch, qty }) => (
                      <li key={batch.id} className="flex justify-between py-1.5">
                        <span className="font-mono text-slate-700">{batch.batchNumber}
                          <span className="ml-2 font-sans text-slate-400">received {batch.receivedDate || batch.mfgDate || '—'}</span>
                        </span>
                        <span className="font-semibold text-slate-900">{qty} {line.unit}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <table className="w-full text-xs">
                    <tbody>
                      {list.map((b) => (
                        <tr key={b.id} className="border-t border-slate-100">
                          <td className="px-3 py-1.5 font-mono text-slate-700">{b.batchNumber}</td>
                          <td className="px-3 py-1.5 text-slate-500">{b.receivedDate || b.mfgDate || '—'}</td>
                          <td className="px-3 py-1.5 text-right text-slate-700">{b.currentQty} {line.unit}</td>
                          <td className="px-3 py-1.5 text-right">
                            <input type="number" min={0} max={b.currentQty} step="any"
                              value={picked[line.id]?.[b.id] ?? ''}
                              onKeyDown={(e) => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }}
                              onChange={(e) => setPicked((p) => ({ ...p, [line.id]: { ...(p[line.id] || {}), [b.id]: e.target.value } }))}
                              className="w-24 h-7 px-2 rounded-md border border-slate-200 text-right focus:outline-none focus:border-indigo-500" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <button type="button" onClick={onCancel} disabled={busy}
            className="h-9 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-white cursor-pointer">
            Cancel
          </button>
          <button type="button" onClick={confirm} disabled={!ready || busy}
            className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center gap-1.5">
            {busy ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-check-line" />}
            {partial ? 'Confirm Partial Issue' : 'Confirm Issue'}
          </button>
        </div>
      </div>
    </div>
  );
}
